import React from 'react';
import './Carousel.css';

// Desktop arrows appear at this width. Phones keep swipe-only scrolling.
const DESKTOP_ARROW_MIN_WIDTH = 600;

class Carousel extends React.Component {
    constructor(props) {
        super(props);

        const slides = props.slides || [];
        const desktop = typeof window !== 'undefined' && window.innerWidth >= DESKTOP_ARROW_MIN_WIDTH;

        this.state = {
            canScrollPrev: false,
            // Assume a multi-card row overflows until layout says otherwise, so the arrow doesn't pop in late.
            canScrollNext: desktop && slides.length > 1,
            showArrows: desktop
        };

        this.scrollerRef = React.createRef();
        // Where a click-sequence is heading. Live scrollLeft lags during smooth scroll.
        this.scrollTarget = null;

        this.getSlideWidth = this.getSlideWidth.bind(this);
        this.updateArrows = this.updateArrows.bind(this);
        this.scrollBySlide = this.scrollBySlide.bind(this);
        this.handleScrollerScroll = this.handleScrollerScroll.bind(this);
        this.handleUserScroll = this.handleUserScroll.bind(this);
        this.handleResize = this.handleResize.bind(this);
    }

    componentDidMount() {
        this._isMounted = true;
        this.updateArrows();

        window.addEventListener('resize', this.handleResize);

        // Card images and late-arriving slides change whether the row overflows.
        const el = this.scrollerRef.current;
        if (el && typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => this.updateArrows());
            this.resizeObserver.observe(el);
            if (el.firstElementChild) {
                this.resizeObserver.observe(el.firstElementChild);
            }
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.slides !== this.props.slides) {
            this.scrollTarget = null;
            this.updateArrows();
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        window.removeEventListener('resize', this.handleResize);
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    // Match the previous inline widths when a factor is provided. Otherwise CSS sets the width.
    getSlideWidth() {
        const factor = this.props.main_width_factor;
        const slides = this.props.slides || [];
        if (typeof factor !== 'number' || slides.length === 0) {
            return null;
        }
        const base = window.innerWidth * factor;
        return slides.length > 1 ? base * 0.8 : base - 3;
    }

    // Distance from one card to the next, including the gap between them.
    getSlideStep(el) {
        const slides = el.querySelectorAll('.carousel_slide');
        if (slides.length >= 2) {
            const first = slides[0].getBoundingClientRect();
            const second = slides[1].getBoundingClientRect();
            const step = second.left - first.left;
            if (step > 0) {
                return step;
            }
        }
        if (slides.length === 1) {
            return slides[0].getBoundingClientRect().width;
        }
        return el.clientWidth;
    }

    updateArrows() {
        if (!this._isMounted) {
            return;
        }
        const el = this.scrollerRef.current;
        const showArrows = window.innerWidth >= DESKTOP_ARROW_MIN_WIDTH;
        if (!el) {
            return;
        }

        const maxScroll = el.scrollWidth - el.clientWidth;
        const canScrollPrev = el.scrollLeft > 4;
        const canScrollNext = maxScroll > 4 && el.scrollLeft < maxScroll - 4;

        if (
            canScrollPrev !== this.state.canScrollPrev ||
            canScrollNext !== this.state.canScrollNext ||
            showArrows !== this.state.showArrows
        ) {
            this.setState({ canScrollPrev, canScrollNext, showArrows });
        }
    }

    handleScrollerScroll() {
        this.updateArrows();
    }

    // Trackpad or wheel movement should become the new starting point for the next click.
    handleUserScroll() {
        this.scrollTarget = null;
    }

    handleResize() {
        this.scrollTarget = null;
        this.updateArrows();
    }

    scrollBySlide(direction) {
        const el = this.scrollerRef.current;
        if (!el) {
            return;
        }

        const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
        const step = this.getSlideStep(el);
        if (step <= 0) {
            return;
        }

        // Ignore a stale target once the row has caught up, or the user scrolled themselves.
        if (this.scrollTarget == null || Math.abs(this.scrollTarget - el.scrollLeft) < 2) {
            this.scrollTarget = el.scrollLeft;
        }

        const next = Math.min(maxScroll, Math.max(0, this.scrollTarget + direction * step));
        this.scrollTarget = next;
        el.scrollTo({ left: next, behavior: 'smooth' });
    }

    render() {
        const slides = this.props.slides || [];
        const slideWidth = this.getSlideWidth();
        const { canScrollPrev, canScrollNext, showArrows } = this.state;

        return (
            <div className='carousel_master_container'>
                <div
                    className='carousel_slider_container'
                    ref={this.scrollerRef}
                    onScroll={this.handleScrollerScroll}
                    onWheel={this.handleUserScroll}
                    onTouchStart={this.handleUserScroll}
                >
                    <div className='carousel_slider_content'>
                        {slides.map(slide =>
                            <div
                                key={slide.key}
                                className='carousel_slide'
                                style={slideWidth ? { width: `${slideWidth}px` } : undefined}
                            >
                                {slide}
                            </div>
                        )}
                    </div>
                </div>

                {showArrows && canScrollPrev ?
                    <button
                        type='button'
                        className='carousel_nav carousel_nav_prev'
                        aria-label='Show previous'
                        onClick={() => this.scrollBySlide(-1)}
                    >
                        <i className='material-icons' aria-hidden='true'>chevron_left</i>
                    </button>
                    : null}

                {showArrows && canScrollNext ?
                    <button
                        type='button'
                        className='carousel_nav carousel_nav_next'
                        aria-label='Show next'
                        onClick={() => this.scrollBySlide(1)}
                    >
                        <i className='material-icons' aria-hidden='true'>chevron_right</i>
                    </button>
                    : null}
            </div>
        );
    }
}

export default Carousel;
